import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';
import { AdminLayoutComponent } from './layout/admin-layout/admin-layout.component';
import { UsersComponent } from './components/users/users.component';
import { AddEditRolesComponent } from './components/roles/add-edit-roles/add-edit-roles.component';
import { AuthGuard } from './core/guards/auth.guard';
import { ViewRolesComponent } from './components/roles/view-roles/view-roles.component';
import { AddEditUserComponent } from './components/users/add-edit-user/add-edit-user.component';
import { UserProfileComponent } from './components/users/user-profile/user-profile.component';
import { FleetComponent } from './components/fleet/fleet.component';
import { AddEditFleetComponent } from './components/fleet/add-edit-fleet/add-edit-fleet.component';
import { SingleViewFleetComponent } from './components/fleet/single-view-fleet/single-view-fleet.component';
import { AddEditInventoryComponent } from './components/inventory/add-edit-inventory/add-edit-inventory.component';
import { SingleViewInventoryComponent } from './components/inventory/single-view-inventory/single-view-inventory.component';
import { SingleViewRepairsComponent } from './components/Repairs/single-view-repairs/single-view-repairs.component';
import { AddEditServiceComponent } from './components/fleet-services/add-edit-service/add-edit-service.component';
import { SingleViewServiceComponent } from './components/fleet-services/single-view-service/single-view-service.component';
import { AddEditAccidentComponent } from './components/accidents/add-edit-accident/add-edit-accident.component';
import { SingleViewAccidentComponent } from './components/accidents/single-view-accident/single-view-accident.component';
import { AddEditRepairsComponent } from './components/Repairs/add-edit-repairs/add-edit-repairs.component';
import { AddEditDriversComponent } from './components/drivers/add-edit-drivers/add-edit-drivers.component';
import { ViewDriversComponent } from './components/drivers/view-drivers/view-drivers.component';
import { SingleViewDriverComponent } from './components/drivers/single-view-driver/single-view-driver.component';
import { AddEditSupplierComponent } from './components/supplier/add-edit-supplier/add-edit-supplier.component';
import { ViewSuppliersComponent } from './components/supplier/view-suppliers/view-suppliers.component';
import { ViewSingleSupplierComponent } from './components/supplier/view-single-supplier/view-single-supplier.component';
import { ViewCustomerComponent } from './components/customer/view-customer/view-customer.component';
import { AddEditCustomerComponent } from './components/customer/add-edit-customer/add-edit-customer.component';
import { ViewSingleCustomerComponent } from './components/customer/view-single-customer/view-single-customer.component';
import { ViewAgentsComponent } from './components/agents/view-agents/view-agents.component';
import { AddEditAgentComponent } from './components/agents/add-edit-agent/add-edit-agent.component';
import { ViewSingleBookingComponent } from './components/bookings/view-single-booking/view-single-booking.component';
import { ViewBookingComponent } from './components/bookings/view-booking/view-booking.component';
import { AddEditBookingComponent } from './components/bookings/add-edit-booking/add-edit-booking.component';
import { RentReservationComponent } from './components/reservations/rent-reservation/rent-reservation.component';
import { AddEditRentReservationComponent } from './components/reservations/rent-reservation/add-edit-rent-reservation/add-edit-rent-reservation.component';
import { SingleViewRentReservationComponent } from './components/reservations/rent-reservation/single-view-rent-reservation/single-view-rent-reservation.component';
import { OtherReservationComponent } from './components/reservations/other-reservation/other-reservation.component';
import { AddEditOtherReservationComponent } from './components/reservations/other-reservation/add-edit-other-reservation/add-edit-other-reservation.component';
import { SingleViewOtherReservationComponent } from './components/reservations/other-reservation/single-view-other-reservation/single-view-other-reservation.component';
import { canDeactivateGuardService } from './core/guards/deactivate.gaurd';
import { CurrencyConfigurationComponent } from './components/currency-configuration/currency-configuration.component';
import { ViewAgentBookingsComponent } from './components/agents/view-agent-bookings/view-agent-bookings.component';
import { SystemLogComponent } from './components/system-log/system-log.component';

const routes: Routes = [
  { path: '', pathMatch: 'full', redirectTo: '/admin/users' },
  // Handling User authentication
  {
    path: 'auth',
    loadChildren: () =>
      import('./modules/auth/auth.module').then((m) => m.AuthModule),
  },
  // App Components
  {
    path: 'admin',
    component: AdminLayoutComponent,
    canActivate: [AuthGuard],
    children: [
      {
        path: '',
        redirectTo: '/admin/users',
        pathMatch: 'full',
      },
      {
        path: 'users',
        component: UsersComponent,
      },
      {
        path: 'users/add-edit',
        component: AddEditUserComponent,
      },
      {
        path: 'users/add-edit/:id',
        component: AddEditUserComponent,
      },
      {
        path: 'users/roles',
        component: ViewRolesComponent,
      },
      {
        path: 'users/roles/add-edit',
        component: AddEditRolesComponent,
      },
      {
        path: 'users/roles/add-edit/:id',
        component: AddEditRolesComponent,
      },
      {
        path: 'users/profile',
        component: UserProfileComponent,
      },
      {
        path: 'fleets',
        component: FleetComponent,
      },
      {
        path: 'fleets/add-edit',
        component: AddEditFleetComponent,
        canDeactivate: [canDeactivateGuardService],
      },
      {
        path: 'fleets/add-edit/:id',
        component: AddEditFleetComponent,
        canDeactivate: [canDeactivateGuardService],
      },
      {
        path: 'fleets/:id',
        component: SingleViewFleetComponent,
      },
      {
        path: 'fleets/:fleetId/service/add-edit',
        component: AddEditServiceComponent,
        canDeactivate: [canDeactivateGuardService],
      },
      {
        path: 'fleets/:fleetId/service/add-edit/:id',
        component: AddEditServiceComponent,
        canDeactivate: [canDeactivateGuardService],
      },
      {
        path: 'fleets/:fleetId/service/:id',
        component: SingleViewServiceComponent,
      },
      {
        path: 'fleets/:fleetId/inventory/add-edit',
        component: AddEditInventoryComponent,
        canDeactivate: [canDeactivateGuardService],
      },
      {
        path: 'fleets/:fleetId/inventory/:id',
        component: SingleViewInventoryComponent,
      },
      {
        path: 'fleets/:fleetId/repair/add-edit',
        component: AddEditRepairsComponent,
        canDeactivate: [canDeactivateGuardService],
      },
      {
        path: 'fleets/:fleetId/repair/add-edit/:id',
        component: AddEditRepairsComponent,
        canDeactivate: [canDeactivateGuardService],
      },
      {
        path: 'fleets/:fleetId/repair/:id',
        component: SingleViewRepairsComponent,
      },
      {
        path: 'fleets/:fleetId/accidents/add-edit',
        component: AddEditAccidentComponent,
        canDeactivate: [canDeactivateGuardService],
      },
      {
        path: 'fleets/:fleetId/accidents/add-edit/:id',
        component: AddEditAccidentComponent,
        canDeactivate: [canDeactivateGuardService],
      },
      {
        path: 'fleets/:fleetId/accidents/:id',
        component: SingleViewAccidentComponent,
      },
      {
        path: 'drivers',
        component: ViewDriversComponent,
      },
      {
        path: 'drivers/add-edit',
        component: AddEditDriversComponent,
        canDeactivate: [canDeactivateGuardService],
      },
      {
        path: 'drivers/add-edit/:id',
        component: AddEditDriversComponent,
        canDeactivate: [canDeactivateGuardService],
      },
      {
        path: 'drivers/:id',
        component: SingleViewDriverComponent,
      },
      {
        path: 'suppliers',
        component: ViewSuppliersComponent,
      },
      {
        path: 'suppliers/add-edit',
        component: AddEditSupplierComponent,
        canDeactivate: [canDeactivateGuardService],
      },
      {
        path: 'suppliers/add-edit/:id',
        component: AddEditSupplierComponent,
        canDeactivate: [canDeactivateGuardService],
      },
      {
        path: 'suppliers/:id',
        component: ViewSingleSupplierComponent,
      },
      {
        path: 'customers',
        component: ViewCustomerComponent,
      },
      {
        path: 'customers/add-edit',
        component: AddEditCustomerComponent,
        canDeactivate: [canDeactivateGuardService],
      },
      {
        path: 'customers/add-edit/:id',
        component: AddEditCustomerComponent,
        canDeactivate: [canDeactivateGuardService],
      },
      {
        path: 'customers/:id',
        component: ViewSingleCustomerComponent,
      },
      {
        path: 'agents',
        component: ViewAgentsComponent,
      },
      {
        path: 'agents/view-booking',
        component: ViewAgentBookingsComponent,
      },
      {
        path: 'agents/add-edit',
        component: AddEditAgentComponent,
        canDeactivate: [canDeactivateGuardService],
      },
      {
        path: 'agents/add-edit/:id',
        component: AddEditAgentComponent,
        canDeactivate: [canDeactivateGuardService],
      },
      {
        path: 'booking-transfer',
        component: ViewBookingComponent,
      },
      {
        path: 'booking-transfer/add-edit',
        component: AddEditBookingComponent,
        canDeactivate: [canDeactivateGuardService],
      },
      {
        path: 'booking-transfer/add-edit/:id',
        component: AddEditBookingComponent,
        canDeactivate: [canDeactivateGuardService],
      },
      {
        path: 'booking-transfer/:id',
        component: ViewSingleBookingComponent,
      },
      {
        path: 'booking-rent',
        component: RentReservationComponent,
      },
      {
        path: 'booking-rent/add-edit',
        component: AddEditRentReservationComponent,
        canDeactivate: [canDeactivateGuardService],
      },
      {
        path: 'booking-rent/add-edit/:id',
        component: AddEditRentReservationComponent,
        canDeactivate: [canDeactivateGuardService],
      },
      {
        path: 'booking-rent/:id',
        component: SingleViewRentReservationComponent,
      },
      {
        path: 'booking-other',
        component: OtherReservationComponent,
      },
      {
        path: 'booking-other/add-edit',
        component: AddEditOtherReservationComponent,
        canDeactivate: [canDeactivateGuardService],
      },
      {
        path: 'booking-other/add-edit/:id',
        component: AddEditOtherReservationComponent,
        canDeactivate: [canDeactivateGuardService],
      },
      {
        path: 'booking-other/:id',
        component: SingleViewOtherReservationComponent,
      },
      {
        path: 'currency-config',
        component: CurrencyConfigurationComponent,
      },
      {
        path: 'system-log',
        component: SystemLogComponent,
      },
    ],
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      scrollPositionRestoration: 'enabled',
      scrollOffset: [0, 0],
      anchorScrolling: 'disabled',
    }),
  ],
  // imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
